import { css, cx } from "@emotion/css";
import { forwardRef, useImperativeHandle, useMemo, useRef, useCallback, useEffect, Fragment } from "react";
import { Avatar } from "~/components/Avatar";
import { BsLockFill } from "react-icons/bs";
import { RecordPointer, ThreadVisibility } from "libs/schema";
import { MentionableUser } from "~/observables/observeMentionableUsers";
import { MentionableGroup } from "~/observables/observeMentionableGroups";
import { throwUnreachableCaseError, UnreachableCaseError } from "libs/errors";
import {
  getMentionQueryWithoutPrefix,
  useEditorMentionContext,
  SectionWrapper,
  SectionHeader,
  Option,
  IMentionAttributes,
  MentionSuggestion,
  useOnSuggestionDropdownOpen,
  getMentionExtensionStorage,
} from "./utils";
import { renderGroupName } from "~/utils/tag-utils";
import { ParentComponent } from "~/utils/type-helpers";
import { useObservable } from "observable-hooks";
import { useClientEnvironment } from "~/environment/ClientEnvironmentContext";
import { combineLatest, distinctUntilChanged, map, switchMap } from "rxjs";
import { useAuthGuardContext } from "~/route-guards/withAuthGuard";
import { Editor } from "@tiptap/react";
import { ObserveOptions } from "~/environment/RecordLoader";
import { stringComparer } from "libs/comparers";
import { applyAdditionalMentionSuggestionWeights } from "~/components/forms/tiptap/suggestion-utils";
import commandScore from "command-score";
import { numberComparer } from "libs/comparers";
import { useLoadingObservable } from "~/hooks/useLoadingObservable";
import { IListRef, List, ListScrollbox, useFocusFirstEntryOnQueryChange } from "~/components/list";
import { IndefiniteLoadingBar } from "~/components/AppLoadingBar";
import { SuggestionProps } from "@tiptap/suggestion";
import { observeMentionDropdownData } from "./observeMentionDropdownData";

/**
 * This code was largely taken from https://tiptap.dev/api/nodes/mention#usage
 */

export const MentionDropdown = forwardRef<
  { onKeyDown(o: { event: KeyboardEvent }): boolean | undefined },
  SuggestionProps<MentionSuggestion> & {
    allowMentioningDeactivatedUsers: boolean;
  }
>((props, forwardedRef) => {
  const listRef = useRef<IListRef<MentionSuggestion>>(null);
  const { restrictToVisibility } = useEditorMentionContext();

  const suggestions = useSuggestions({
    editor: props.editor,
    query: props.query,
    includeDeactivatedUsers: props.allowMentioningDeactivatedUsers,
  });

  useOnSuggestionDropdownOpen();

  const onSelect = useCallback(
    (event: { entry: MentionSuggestion }) => {
      const mentionProps: Omit<IMentionAttributes, "priority"> = {
        id: event.entry.id,
        label:
          event.entry.type === "group" ? renderGroupName(event.entry.record)
          : event.entry.type === "user" ? event.entry.profile.name
          : throwUnreachableCaseError(event.entry),
        subject: event.entry.type,
      };

      // The SuggestionProps are typed as requiring props.command to
      // receive the same type as props.items[0] but, in actually,
      // they don't need to be the same and we aren't passing the
      // same interface.
      props.command(mentionProps as any);
    },
    [props.command],
  );

  const enterHandler = (e?: Event) => {
    const list = listRef.current!;

    const item = list.focusableOrActiveEntry();

    if (!item) return;

    switch (item.data.type) {
      case "user": {
        onSelect({ entry: item.data });
        return;
      }
      case "group": {
        const isDisabled =
          restrictToVisibility === null ? false
          : item.data.isPrivate ? restrictToVisibility === "SHARED"
          : restrictToVisibility === "PRIVATE";

        if (isDisabled) {
          alert(
            item.data.isPrivate ?
              "Can only add private groups to private threads."
            : "Can only add shared groups to shared threads.",
          );

          e?.preventDefault();
        } else {
          onSelect({ entry: item.data });
        }

        return;
      }
      default: {
        throw new UnreachableCaseError(item.data);
      }
    }
  };

  useImperativeHandle(forwardedRef, () => ({
    onKeyDown: ({ event }) => {
      const list = listRef.current!;

      if (event.key === "ArrowUp") {
        list.focusPrevEntry();
        return true;
      }

      if (event.key === "ArrowDown") {
        list.focusNextEntry();
        return true;
      }

      if (event.key === "Enter") {
        event.stopPropagation();
        enterHandler(event);
        return true;
      }

      return false;
    },
  }));

  useFocusFirstEntryOnQueryChange({ listRef, query: props.query });

  return (
    <ListScrollbox>
      <div
        data-testid="editor-mention-dropdown"
        className={cx("bg-white rounded overflow-hidden", `w-[330px] pb-3`, mentionListStyles)}
      >
        <List ref={listRef} mode="active-descendent" onEntryAction={onSelect}>
          <div role="list">
            {suggestions.isLoading && (
              <div className="fixed top-0 left-0 w-full">
                <IndefiniteLoadingBar />
              </div>
            )}

            <SuggestionSections suggestions={suggestions} restrictGroupsToVisibility={restrictToVisibility} />
          </div>
        </List>
      </div>
    </ListScrollbox>
  );
});

const mentionListStyles = css`
  box-shadow:
    rgba(15, 15, 15, 0.05) 0px 0px 0px 1px,
    rgba(15, 15, 15, 0.1) 0px 3px 6px,
    rgba(15, 15, 15, 0.2) 0px 9px 24px;
`;

/* -----------------------------------------------------------------------------------------------*/

const SuggestionSections: ParentComponent<{
  suggestions: UseSuggestionsResult;
  restrictGroupsToVisibility: ThreadVisibility | null;
}> = (props) => {
  const { suggestions, restrictGroupsToVisibility } = props;

  const sections = useMemo(() => {
    const nonEmptySections: Array<{ id: string; content: React.ReactNode }> = [];

    if (suggestions.people.length > 0) {
      nonEmptySections.push({ id: "people", content: <PeopleList people={suggestions.people} /> });
    }

    if (suggestions.groups.length > 0) {
      nonEmptySections.push({
        id: "groups",
        content: <GroupList groups={suggestions.groups} restrictToVisibility={restrictGroupsToVisibility} />,
      });
    }

    if (suggestions.deactivatedUsers.length > 0) {
      nonEmptySections.push({
        id: "deactivated",
        content: <PeopleList people={suggestions.deactivatedUsers} areDeactivated />,
      });
    }

    return nonEmptySections;
  }, [suggestions]);

  return (
    <>
      {sections.map((section, index) => (
        <Fragment key={section.id}>
          {index > 0 && <hr className="text-slate-5 mt-3 mb-1" />}
          {section.content}
        </Fragment>
      ))}
    </>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const PeopleList: ParentComponent<{
  people: MentionableUser[];
  areDeactivated?: boolean;
}> = (props) => {
  if (props.people.length === 0) return null;

  return (
    <SectionWrapper>
      <SectionHeader label={props.areDeactivated ? "Deactivated Users" : "People"} />

      {props.people.map((person, index) => (
        <Option key={person.id} item={person} relativeOrder={index}>
          <Avatar
            label={person.profile.name}
            photoURL={person.profile.photo_url}
            fontSize="11px"
            width="1.5rem"
            className="mx-3"
          />
          <span className="truncate">{person.profile.name}</span>
        </Option>
      ))}
    </SectionWrapper>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const GroupList: ParentComponent<{
  groups: MentionableGroup[];
  restrictToVisibility: ThreadVisibility | null;
}> = (props) => {
  if (props.groups.length === 0) return null;

  return (
    <SectionWrapper>
      <SectionHeader label="Groups" />

      {props.groups.map((group, index) => {
        const isDisabled =
          props.restrictToVisibility === null ? false
          : group.isPrivate ? props.restrictToVisibility === "SHARED"
          : props.restrictToVisibility === "PRIVATE";

        return (
          <Option key={group.id} item={group} isDisabled={isDisabled} relativeOrder={index}>
            <div className={cx(group.record.archived_at ? "text-slate-9" : null)}>
              <span className="inline-flex items-center shrink whitespace-nowrap overflow-hidden">
                <span className="text-xl mx-3 w-6 text-center">{group.record.icon}</span>
                <span className="truncate">{group.record.name}</span>
                {group.isPrivate && <BsLockFill className="ml-1 scale-75" />}
                {group.record.archived_at && <span className="ml-1"> (Archived)</span>}
              </span>
            </div>
          </Option>
        );
      })}
    </SectionWrapper>
  );
};

/* -----------------------------------------------------------------------------------------------*/

/**
 * This hook maintains proper subscriptions to make opening the mentions list speedy.
 */
export function useSubscribeToMentionDropdownData(props: {
  threadId: string | null | undefined;
  includeDeactivatedUsers?: boolean;
}) {
  const environment = useClientEnvironment();
  const { currentUserId } = useAuthGuardContext();

  useEffect(() => {
    const sub = observeMentionDropdownData(environment, {
      currentUserId,
      threadId: props.threadId,
      includeDeactivatedUsers: props.includeDeactivatedUsers,
    }).subscribe();

    return () => sub.unsubscribe();
  }, [props.threadId, props.includeDeactivatedUsers, environment, currentUserId]);
}

/* -----------------------------------------------------------------------------------------------*/

type UseSuggestionsResult = ReturnType<typeof useSuggestions>;

function useSuggestions(props: { editor: Editor; query: string; includeDeactivatedUsers: boolean }) {
  const environment = useClientEnvironment();
  const { currentUserId } = useAuthGuardContext();

  const mentionData$ = useObservable(
    (input$) =>
      input$.pipe(
        switchMap(([environment, currentUserId, includeDeactivatedUsers]) => {
          // We can use the cached data here as we subscribe to the queries below with the
          // useMentionExtensionSubscriptions hook
          const options: ObserveOptions = {};
          const { threadId } = getMentionExtensionStorage(props.editor);

          return observeMentionDropdownData(environment, { currentUserId, threadId, includeDeactivatedUsers }, options);
        }),
      ),
    [environment, currentUserId, props.includeDeactivatedUsers],
  );

  const query$ = useObservable(
    (input$) =>
      input$.pipe(
        map(([rawQuery]) => getMentionQueryWithoutPrefix(rawQuery)),
        distinctUntilChanged(),
      ),
    [props.query],
  );

  const initialValue = useMemo(
    () => ({
      people: [] as MentionableUser[],
      groups: [] as MentionableGroup[],
      deactivatedUsers: [] as MentionableUser[],
      isLoading: true,
    }),
    [],
  );

  return useLoadingObservable({
    initialValue,
    fn(inputs$) {
      return inputs$.pipe(
        switchMap(([editor, mentionData$, query$]) => {
          return combineLatest([mentionData$, query$]).pipe(
            map(([mentionData, query]) => {
              const frequencyDictionary = mentionData.mentions || {};

              const { mentionWeightAdjustments } = getMentionExtensionStorage(editor);

              const results = {
                people: [] as MentionableUser[],
                groups: [] as MentionableGroup[],
                deactivatedUsers: [] as MentionableUser[],
              };

              const filterItems = <T extends { type: "group" | "user"; id: string }>(
                items: T[],
                query: string,
                getLabel: (o: T) => string,
              ) =>
                items
                  .map((c) => {
                    const label = getLabel(c);
                    let score = commandScore(label, query);

                    if (score !== 0) {
                      const pointer: RecordPointer =
                        c.type === "group" ? { table: "tag", id: c.id }
                        : c.type === "user" ? { table: "user_profile", id: c.id }
                        : throwUnreachableCaseError(c.type);

                      score = applyAdditionalMentionSuggestionWeights({
                        score,
                        pointer,
                        frequencyDictionary,
                        mentionWeightAdjustments,
                      });
                    }

                    return {
                      option: c,
                      score,
                    };
                  })
                  .sort((a, b) => b.score - a.score)
                  .slice(0, 9)
                  .filter((r) => r.score > 0)
                  .map((r) => ({ ...r.option }));

              if (query.startsWith("#")) {
                results.groups = filterItems(mentionData.groups, query.slice(1), (c) => c.record.name);
              } else if (query) {
                results.people = filterItems(mentionData.users, query, (m) => m.profile.name);
                results.groups = filterItems(mentionData.groups, query, (c) => c.record.name);
                results.deactivatedUsers = filterItems(mentionData.deactivatedUsers, query, (m) => m.profile.name);
              } else {
                results.people = mentionData.users
                  .map((user) => {
                    const pointer: RecordPointer = {
                      table: "user_profile",
                      id: user.id,
                    };

                    return {
                      user,
                      score: applyAdditionalMentionSuggestionWeights({
                        score: 0,
                        pointer,
                        frequencyDictionary,
                        mentionWeightAdjustments,
                      }),
                    };
                  })
                  .sort(
                    (a, b) =>
                      numberComparer(b.score, a.score) ||
                      stringComparer(a.user.profile.name, b.user.profile.name) ||
                      stringComparer(a.user.id, b.user.id),
                  )
                  .slice(0, 5)
                  .map((m) => m.user);

                results.groups = mentionData.groups
                  .map((group) => {
                    const pointer: RecordPointer = {
                      table: "tag",
                      id: group.id,
                    };

                    return {
                      group,
                      score: applyAdditionalMentionSuggestionWeights({
                        score: 0,
                        pointer,
                        frequencyDictionary,
                        mentionWeightAdjustments,
                      }),
                    };
                  })
                  .sort((a, b) => {
                    // Archived groups should come after non-archived groups
                    if (a.group.record.archived_at && !b.group.record.archived_at) {
                      return 1;
                    } else if (!a.group.record.archived_at && b.group.record.archived_at) {
                      return -1;
                    }

                    return (
                      numberComparer(b.score, a.score) ||
                      stringComparer(a.group.record.name, b.group.record.name) ||
                      stringComparer(a.group.id, b.group.id)
                    );
                  })
                  .slice(0, 5)
                  .map((m) => m.group);
              }

              return { ...results, isLoading: mentionData.isLoading };
            }),
          );
        }),
      );
    },
    deps: [props.editor, mentionData$, query$],
  });
}

/* -----------------------------------------------------------------------------------------------*/
